export const EXCAVATION_PERMIT_LABEL = "Excavation";

export const emptyApprovalRow = () => ({
  department_id: "",
  department_name: "",
  approval_order: "",
  is_required: "1",
  is_parallel: "0",
});

export const emptyReviewerRow = () => ({
  reviewer_id: "",
  user_id: "",
  user_name: "",
  user_email: "",
  is_primary: "0",
  can_approve: "1",
  can_reject: "1",
});

export const normalizeText = (value = "") =>
  value.toString().trim().toLowerCase();

export const findExcavationPermitType = (permitTypes = []) =>
  permitTypes.find((permitType) =>
    normalizeText(permitType.work_permit_name).includes("excavation")
  );

export const buildDepartmentOptions = (departments = []) =>
  departments.map((department) => ({
    value: String(department.department_id || department.ID || department.id || ""),
    label: department.DepartmentName || department.department_name,
  }));

export const buildUserOptions = (users = []) =>
  users.map((user) => ({
    value: String(user.user_id || user.ID || user.id || ""),
    label: [user.Name || user.user_name, user.Email || user.user_email]
      .filter(Boolean)
      .join(" - "),
    user_name: user.Name || user.user_name || "",
    user_email: user.Email || user.user_email || "",
  }));

export const getReviewerUserId = (row = {}) =>
  String(row.user_id || row.userId || row.ID || row.id || row.value || "");

export const buildApprovalRows = (departments = []) =>
  departments.length
    ? departments.map((department) => ({
        department_id: department.department_id || "",
        department_name: department.department_name || "",
        approval_order:
          department.approval_order === 0 || department.approval_order
            ? String(department.approval_order)
            : "",
        is_required: String(
          department.is_required === 0 || department.is_required === "0" ? 0 : 1
        ),
        is_parallel: String(
          department.is_parallel === 1 || department.is_parallel === "1" ? 1 : 0
        ),
      }))
    : [emptyApprovalRow()];

export const validateApprovalRows = (rows = []) => {
  const errors = {};
  const selectedDepartments = new Set();
  const orderGroups = {};

  rows.forEach((row, index) => {
    if (!row.department_id) {
      errors[`department_id_${index}`] = "Department is required.";
    }

    if (!row.approval_order || Number(row.approval_order) < 1) {
      errors[`approval_order_${index}`] = "Approval order is required.";
    }

    if (row.department_id) {
      if (selectedDepartments.has(row.department_id)) {
        errors[`department_id_${index}`] = "Department is already selected.";
      }
      selectedDepartments.add(row.department_id);
    }

    if (row.approval_order) {
      const order = String(row.approval_order);
      orderGroups[order] = orderGroups[order] || [];
      orderGroups[order].push({ ...row, index });
    }
  });

  Object.values(orderGroups).forEach((group) => {
    if (group.length > 1 && group.some((row) => row.is_parallel !== "1")) {
      group.forEach((row) => {
        errors[`is_parallel_${row.index}`] =
          "Departments with the same order must be parallel.";
      });
    }
  });

  return errors;
};

export const buildApprovalPayload = (permitTypeId, rows = []) => ({
  permit_type_id: permitTypeId,
  departments: rows.map((row) => ({
    department_id: row.department_id,
    approval_order: Number(row.approval_order),
    is_required: Number(row.is_required),
    is_parallel: Number(row.is_parallel),
  })),
});

export const buildReviewerRows = (reviewers = []) =>
  reviewers.length
    ? reviewers.map((reviewer) => ({
        reviewer_id: reviewer.id || reviewer.reviewer_id || "",
        user_id: reviewer.user_id ? String(reviewer.user_id) : "",
        original_user_id: reviewer.user_id ? String(reviewer.user_id) : "",
        user_name: reviewer.user_name || "",
        user_email: reviewer.user_email || "",
        is_primary: String(
          reviewer.is_primary === 1 || reviewer.is_primary === "1" ? 1 : 0
        ),
        original_is_primary: String(
          reviewer.is_primary === 1 || reviewer.is_primary === "1" ? 1 : 0
        ),
        can_approve: String(
          reviewer.can_approve === 0 || reviewer.can_approve === "0" ? 0 : 1
        ),
        original_can_approve: String(
          reviewer.can_approve === 0 || reviewer.can_approve === "0" ? 0 : 1
        ),
        can_reject: String(
          reviewer.can_reject === 0 || reviewer.can_reject === "0" ? 0 : 1
        ),
        original_can_reject: String(
          reviewer.can_reject === 0 || reviewer.can_reject === "0" ? 0 : 1
        ),
      }))
    : [emptyReviewerRow()];

export const validateReviewerRows = (rows = []) => {
  const errors = {};
  const selectedUsers = new Set();

  rows.forEach((row, index) => {
    const userId = getReviewerUserId(row);

    if (!userId) {
      errors[`user_id_${index}`] = "Reviewer is required.";
    }

    if (userId) {
      if (selectedUsers.has(userId)) {
        errors[`user_id_${index}`] = "Reviewer is already selected.";
      }
      selectedUsers.add(userId);
    }
  });

  return errors;
};

export const buildReviewerPayload = (departmentId, rows = []) => ({
  department_id: departmentId,
  reviewers: rows.map((row) => ({
    user_id: getReviewerUserId(row),
    is_primary: Number(row.is_primary),
    can_approve: Number(row.can_approve),
    can_reject: Number(row.can_reject),
  })),
});
